import { Dispatch, ReactNode, SetStateAction, useState, useEffect, Fragment, useRef } from "react";
import { Popover, Transition } from "@headlessui/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import Counter from "components/counter/Counter";
import CustomSwitch from "components/form/CustomSwitch";
import CloseIcon from "components/icons-svg/CloseIcon";
import CVADrawer from "components/modals/common/CVADrawer";
import Divider from "components/utilities/Divider";
import { useAppDispatch } from "stores/hooks";
import { markSearch } from "stores/search/searchSlice";
import { markSearchFlow } from "stores/ui/uiSlice";
import { useDisclosure } from "utils/hooks/useDisclosure";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { useOnClickOutside } from "utils/hooks/useOnClickOutside";
import { removeQueryParameters, updateQueryStringParameter } from "utils/url";
export interface GuestPopoverType {
  children: (x: {
    adult: number;
    child: number;
    pets: boolean;
    petNumber: number;
  }) => ReactNode | string;
  initalState?: {
    adultCount: number;
    childCount: number;
    petsAllowed: boolean;
    petNumber: number;
  };
  isDetailPage?: boolean;
  maxGuestCount: number;
  onApply?: (x: {
    adult: number;
    child: number;
    pets: boolean;
    petNumber: number;
  }) => void;
  onClear?: () => void;
  petAllowed: boolean;
  childAllowed: boolean;
  maxPetsCount?: number;
  minimumAge: number;
  placeURL?: string;
}
type GuestContentType = {
  guestOptions: {
    adult: number;
    child: number;
    pets: boolean;
    petNumber: number;
  };
  isDetailPage: boolean;
  maxGuestCount: number;
  petAllowed: boolean;
  childAllowed: boolean;
  maxPetsCount: number;
  minimumAge: number;
  setGuestOptions: Dispatch<SetStateAction<{
    adult: number;
    child: number;
    pets: boolean;
    petNumber: number;
  }>>;
  placeURL?: string;
};
const GuestContent = ({
  guestOptions,
  isDetailPage,
  setGuestOptions,
  maxGuestCount,
  petAllowed,
  maxPetsCount,
  childAllowed,
  minimumAge,
  placeURL
}: GuestContentType) => {
  const c = useTranslations("common");
  return <div className='flex flex-col gap-6' data-sentry-component="GuestContent" data-sentry-source-file="GuestPopoverDetailBooking.tsx">
      <div className='flex items-center justify-between'>
        <div>
          <p className='mb-2 text-base font-semibold leading-[16px] tracking-[-0.16px] text-[#26262B]'>
            {c("words.adults")}
            {/* Adults */}
          </p>
          <span className='text-sm font-medium leading-[14.63px] text-[#53535D]'>
            Ages 13 and above
            {/*{c("words.ages13orabove")}*/}
            {/* Ages 13 or above */}
          </span>
        </div>
        <div>
          <Counter key='Adults' isDetailPage={isDetailPage} value={guestOptions.adult} decrementCounter={() => setGuestOptions(() => ({
          ...guestOptions,
          adult: isDetailPage && guestOptions.adult === 1 ? guestOptions.adult : guestOptions.adult - 1
        }))} incrementCounter={() => setGuestOptions(() => ({
          ...guestOptions,
          adult: guestOptions.adult + 1
        }))} max={maxGuestCount - guestOptions.child} data-sentry-element="Counter" data-sentry-source-file="GuestPopoverDetailBooking.tsx" />
        </div>
      </div>
      {childAllowed && <div className='flex items-center justify-between'>
          <div>
            <p className='mb-2 text-base font-semibold leading-[16px] tracking-[-0.16px] text-[#26262B]'>
              {c("words.children")}
              {/* Children */}
            </p>
            <span className='text-sm font-medium leading-[14.63px] text-[#53535D]'>
              Ages 12 and under
              {/*{c("words.ages2_12")} /!* Ages 2-12 *!/*/}
            </span>
          </div>
          <div>
            <Counter key='Children' value={guestOptions.child} decrementCounter={() => setGuestOptions(() => ({
          ...guestOptions,
          child: guestOptions.child - 1
        }))} incrementCounter={() => setGuestOptions(() => ({
          ...guestOptions,
          child: guestOptions.child + 1
        }))} max={maxGuestCount - guestOptions.adult} />
          </div>
        </div>}
      {petAllowed && maxPetsCount <= 1 && <div className='flex items-center justify-between'>
          <div className=''>
            <p className='mb-2 text-base font-semibold leading-[16px] tracking-[-0.16px] text-[#26262B]'>
              {c("words.pets")}
              {/* Pets */}
            </p>
            <span className='text-sm font-medium leading-[14.63px] text-[#53535D]'>
              Are you bringing a pet?
            </span>
          </div>
          <CustomSwitch id='Pets' checked={guestOptions.pets} onChange={() => setGuestOptions(() => ({
        ...guestOptions,
        pets: !guestOptions.pets
      }))} />
        </div>}
      {petAllowed && maxPetsCount > 1 && <div className='flex items-center justify-between'>
          <div>
            <p className='text-sm font-semibold'>
              {c("words.pets")}
              {/* Pets */}
            </p>
          </div>
          <div>
            <Counter key='Pets' value={guestOptions.petNumber} decrementCounter={() => setGuestOptions(() => ({
          ...guestOptions,
          petNumber: guestOptions.petNumber - 1,
          pets: guestOptions.petNumber - 1 > 0
        }))} incrementCounter={() => setGuestOptions(() => ({
          ...guestOptions,
          petNumber: guestOptions.petNumber + 1,
          pets: guestOptions.petNumber + 1 > 0
        }))} max={maxPetsCount} />
          </div>
        </div>}
      {!petAllowed && isDetailPage ? <div>
          <p className='mb-2 text-base font-semibold leading-[16px] tracking-[-0.16px] text-[#26262B]'>
            No pets allowed
          </p>
          {placeURL && <Link href={placeURL} target='_blank' className='underline-advanced whitespace-normal text-xs font-medium leading-[1.3] text-[#53535D] underline' rel='noreferrer' data-track-search-origin='property detail page'>
              Search for pet friendly properties in the same area
            </Link>}
        </div> : null}

      <p className='mt-4 text-sm font-medium leading-[1.5] text-[#53535D]'>
        {`${maxGuestCount} guests maximum.`}
        <br />
        {`${minimumAge && minimumAge > 0 ? `Minimum age to rent is ${minimumAge}.` : ""}`}
        <br />
        {`${petAllowed && maxPetsCount > 1 ? `If you are bringing more than ${maxPetsCount} pets, please let your host know.` : ""}`}
      </p>
    </div>;
};
const GuestPopoverInDetailBooking = (props: GuestPopoverType) => {
  const [isSmallerThan460] = useMediaQuery("(max-width: 460px)");
  // pass the button to this component as a children to trigger popover.
  const {
    children,
    onApply,
    initalState,
    maxGuestCount,
    isDetailPage = false,
    petAllowed = false,
    maxPetsCount = 0,
    childAllowed = false,
    minimumAge,
    placeURL
  } = props;
  const {
    isOpen,
    onToggle,
    onClose
  } = useDisclosure();
  const pathname = usePathname();
  const isPaymentPage = pathname?.startsWith("/payment");
  const dispatch = useAppDispatch();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [guestOptions, setGuestOptions] = useState({
    adult: initalState?.adultCount || 0,
    child: initalState?.childCount || 0,
    pets: initalState?.petsAllowed || false,
    petNumber: initalState?.petNumber || 0
  });
  const [isOpenGuestDrawer, setIsOpenGuestDrawer] = useState(false);
  const initialGuestValue = {
    adult: initalState?.adultCount || 0,
    child: initalState?.childCount || 0,
    pets: initalState?.petsAllowed || false,
    petNumber: initalState?.petNumber || 0
  };
  const updateURL = () => {
    const adultUrl = updateQueryStringParameter(window.location.href, isPaymentPage ? "adultNumber" : "adultsCount", guestOptions.adult);
    const childURL = updateQueryStringParameter(adultUrl, isPaymentPage ? "childNumber" : "childrenCount", guestOptions.child);
    const petNumerURL = updateQueryStringParameter(childURL, "petNumber", guestOptions.petNumber);
    const petUrl = updateQueryStringParameter(petNumerURL, isPaymentPage ? "pet" : "suitablePet", `${guestOptions.pets ? "true" : "false"}`);
    const updatedURL = removeQueryParameters(petUrl, ["quoteId"]);
    window.history.pushState({
      path: updatedURL
    }, "", updatedURL);
  };
  useEffect(() => {
    setGuestOptions(initialGuestValue);
  }, [isOpen, initalState]);
  const guestPopoverCloseHandler = () => {
    if (onApply) {
      onApply(guestOptions);
      updateURL();
    }
    dispatch(markSearchFlow({
      currentSearchTab: null
    }));
    onClose();
  };
  const guestDrawerCloseHandler = () => {
    if (onApply) {
      onApply(guestOptions);
      updateURL();
    }
    dispatch(markSearch({
      adultCount: guestOptions.adult,
      childrenCount: guestOptions.child,
      petAllowed: guestOptions.pets,
      numberOfGuest: guestOptions.adult + guestOptions.child,
      childAllowed: guestOptions.child > 0,
      page: 0
    }));
    setIsOpenGuestDrawer(false);
  };
  useOnClickOutside(popoverRef, () => {
    if (isOpen) {
      guestPopoverCloseHandler();
    }
  });
  return <>
      {isSmallerThan460 ? <>
          <button type='button' onClick={() => setIsOpenGuestDrawer(true)} className='overflow-visible text-ellipsis msm:whitespace-nowrap'>
            {children(guestOptions)}
          </button>
          <CVADrawer isOpen={isOpenGuestDrawer} onClose={() => setIsOpenGuestDrawer(false)} intent='bottom' size='fullScreen' isCloseIcon={false} className='!p-0' disableIOSInset>
            <header className='px-6 pb-4 pt-8 ssm2:px-8'>
              <div className='flex items-center justify-between gap-2'>
                <p className='text-lg font-semibold leading-[18px] tracking-[-0.18px] text-black-900'>
                  Guests
                </p>
                {/* TODO: anisha fix this, close icons should not have onClick, instead it should be wrapper in a button */}
                <CloseIcon className='h-6 w-6' onClick={() => guestDrawerCloseHandler()} />
              </div>
            </header>
            <main className='w-full flex-1 overflow-auto px-6 py-4 ssm2:px-8'>
              <GuestContent setGuestOptions={setGuestOptions} guestOptions={guestOptions} isDetailPage={isDetailPage} maxGuestCount={maxGuestCount} petAllowed={petAllowed} maxPetsCount={maxPetsCount} childAllowed={childAllowed} minimumAge={minimumAge} placeURL={placeURL} />
            </main>
            <Divider />
            <footer>
              <div className='flex w-full items-center justify-between gap-4 px-6 py-4 shadow-[0_-4px_7px_0_rgba(55,56,77,0.08)]'>
                <button type='button' className='btn btn-secondary w-full min-w-[unset] rounded-lg border-[#BCC1D6] text-base font-semibold leading-7 text-[#53535D] msm:min-w-[140px]' onClick={() => guestDrawerCloseHandler()}>
                  Close
                </button>
                {/* <button
                  type='button'
                  className='btn btn-primary w-1/2 min-w-[unset]  rounded-lg px-10 py-3 text-base font-bold leading-7 hover:bg-[#015764] msm:min-w-[140px]'
                  onClick={() => {
                    if (onApply) {
                      onApply(guestOptions)
                      updateURL()
                    }
                    dispatch(
                      markSearch({
                        adultCount: guestOptions.adult,
                        childrenCount: guestOptions.child,
                        petAllowed: guestOptions.pets,
                        numberOfGuest: guestOptions.adult + guestOptions.child,
                        childAllowed: guestOptions.child > 0,
                        page: 0,
                      }),
                    )
                    setIsOpenGuestDrawer(false)
                  }}
                 >
                  {c("words.save")}
                 </button> */}
                {/* Save */}
              </div>
            </footer>
          </CVADrawer>
        </> : <Popover as='div' className='relative inline-block' ref={popoverRef}>
          <button type='button' className='line-clamp-1 w-[auto] overflow-visible text-ellipsis outline-none' onClick={onToggle}>
            {children(guestOptions)}
          </button>
          <Transition as={Fragment} show={isOpen} enter='transition ease-out duration-200' enterFrom='opacity-0 translate-y-1' enterTo='opacity-100 translate-y-0' leave='transition ease-in duration-150' leaveFrom='opacity-100 translate-y-0' leaveTo='opacity-0 translate-y-1'>
            <Popover.Panel id='GuestFilter' className={`${isDetailPage ? "msm:!left-[initial] msm:right-0 msm:!translate-x-[initial]" : ""} ${isPaymentPage ? "md:bottom-9" : "bottom-9"} absolute left-1/2 !z-[12] w-[320px] max-w-[320px] -translate-x-1/2 rounded-xl border border-inherit bg-offWhite p-6 shadow-[-2px_1px_8px_1px_#0000003d] sxm:-translate-x-[28%] md:left-0 md:translate-x-[initial] lg:bottom-[initial] lg:left-[initial] lg:right-0 lg:top-9`}>
              <main>
                <GuestContent setGuestOptions={setGuestOptions} guestOptions={guestOptions} isDetailPage={isDetailPage} maxGuestCount={maxGuestCount} petAllowed={petAllowed} maxPetsCount={maxPetsCount} childAllowed={childAllowed} minimumAge={minimumAge} placeURL={placeURL} />
              </main>
              <footer className='pt-2'>
                <div className='flex items-center justify-end gap-3'>
                  <button type='button' className='text-sm font-bold text-textColor underline' onClick={() => guestPopoverCloseHandler()}>
                    Close
                  </button>
                  {/* <button
                    type='button'
                    className='btn btn-primary h-8'
                    onClick={() => {
                      if (onApply) {
                        onApply(guestOptions)
                        updateURL()
                      }
                      dispatch(
                        markSearchFlow({
                          currentSearchTab: null,
                        }),
                      )
                      onClose()
                    }}
                   >
                    {c("words.apply")}
                   </button> */}
                  {/* Apply */}
                </div>
              </footer>
            </Popover.Panel>
          </Transition>
        </Popover>}
    </>;
};
export default GuestPopoverInDetailBooking;